import { motion } from "framer-motion";
import { PhoneCall, Handshake, LifeBuoy, Megaphone, BarChart3, BookOpen, CalendarClock, FileText, Users, Mail, Server, FolderOpen, CalendarDays, Database, Globe, Plug, LineChart } from "lucide-react";
import { SectionHeading, FadeUp } from "./Reveal";

const AGENTS = [
  { label: "AI Receptionist", Icon: PhoneCall },
  { label: "AI Sales Assistant", Icon: Handshake },
  { label: "AI Support Agent", Icon: LifeBuoy },
  { label: "AI Marketing Agent", Icon: Megaphone },
  { label: "AI Analytics Agent", Icon: BarChart3 },
  { label: "AI Knowledge Assistant", Icon: BookOpen },
  { label: "AI Scheduling Agent", Icon: CalendarClock },
  { label: "AI Document Agent", Icon: FileText },
];

const SYSTEMS = [
  { label: "CRM", Icon: Users }, { label: "Email", Icon: Mail }, { label: "ERP", Icon: Server },
  { label: "Documents", Icon: FolderOpen }, { label: "Calendar", Icon: CalendarDays }, { label: "Databases", Icon: Database },
  { label: "Websites", Icon: Globe }, { label: "APIs", Icon: Plug }, { label: "Analytics", Icon: LineChart },
];

export const AIWorkforce = () => (
  <section id="solutions" data-testid="ai-workforce-section" className="py-24 sm:py-32 bg-jx-night text-white relative overflow-hidden">
    <div className="pointer-events-none absolute top-0 left-1/3 w-[560px] h-[560px] rounded-full bg-jx-purple/30 blur-[140px]" />
    <div className="pointer-events-none absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-jx-magenta/20 blur-[120px]" />

    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHeading
            dark
            testId="ai-workforce"
            overline="AI & Automation"
            title={<>Build an <span className="jx-gradient-text">intelligent workforce.</span></>}
            body="AI should do more than answer questions. JIYONOX designs intelligent agents that can understand context, use tools, connect to business systems and execute real work."
          />
          <FadeUp delay={0.25} className="mt-10">
            <div className="flex items-center gap-4 text-white/50 text-sm">
              <span className="w-10 h-px bg-gradient-to-r from-jx-magenta to-transparent" />
              Agents orchestrated through one intelligent operating layer
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          <FadeUp delay={0.15}>
            <div className="rounded-3xl jx-glass-dark p-6 sm:p-10" data-testid="agent-orchestration-diagram">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-8">
                <div className="flex flex-col gap-2.5">
                  {AGENTS.map((a, i) => (
                    <motion.div
                      key={a.label}
                      initial={{ opacity: 0, x: -24 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      whileHover={{ x: 6 }}
                      data-testid={`agent-chip-${i}`}
                      className="flex items-center gap-2.5 rounded-xl bg-white/[0.06] border border-white/10 px-3 py-2.5 hover:border-jx-magenta/50 transition-colors duration-300"
                    >
                      <a.Icon className="w-4 h-4 text-jx-magenta shrink-0" strokeWidth={1.8} />
                      <span className="text-xs sm:text-sm font-medium text-white/85">{a.label}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="relative flex flex-col items-center py-4">
                  <svg width="60" height="100%" viewBox="0 0 60 400" preserveAspectRatio="none" className="absolute inset-0 h-full w-full opacity-60">
                    <line x1="0" y1="200" x2="60" y2="200" stroke="url(#orchGrad)" strokeWidth="1.5" strokeDasharray="4 6" />
                    {[40, 90, 140, 250, 300, 350].map((y) => (
                      <g key={y}>
                        <line x1="0" y1={y} x2="30" y2="200" stroke="url(#orchGrad)" strokeWidth="1" strokeDasharray="3 5" opacity="0.5" />
                        <line x1="60" y1={y} x2="30" y2="200" stroke="url(#orchGrad)" strokeWidth="1" strokeDasharray="3 5" opacity="0.5" />
                      </g>
                    ))}
                    <defs>
                      <linearGradient id="orchGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#D12D8C" />
                        <stop offset="100%" stopColor="#E86A33" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <motion.div
                    animate={{ boxShadow: ["0 0 24px rgba(209,45,140,0.4)", "0 0 48px rgba(209,45,140,0.7)", "0 0 24px rgba(209,45,140,0.4)"] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="relative z-10 w-16 h-16 rounded-2xl jx-gradient-bg flex items-center justify-center"
                  >
                    <span className="font-display font-extrabold text-white text-[10px] tracking-widest text-center leading-tight">AI<br />CORE</span>
                  </motion.div>
                </div>

                <div className="flex flex-col gap-2.5">
                  {SYSTEMS.map((s, i) => (
                    <motion.div
                      key={s.label}
                      initial={{ opacity: 0, x: 24 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      whileHover={{ x: -6 }}
                      data-testid={`system-chip-${i}`}
                      className="flex items-center gap-2.5 rounded-xl bg-white/[0.04] border border-white/8 px-3 py-2 hover:border-jx-copper/50 transition-colors duration-300"
                    >
                      <s.Icon className="w-3.5 h-3.5 text-jx-copper shrink-0" strokeWidth={1.8} />
                      <span className="text-xs font-medium text-white/70">{s.label}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);
